// src/services/routeClosuresService.js - VERSIÓN CORREGIDA
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { authService } from './authService';
import { enqueue } from './offlineQueue';

// `route_closures` crece 1 doc por técnico y día. Los paneles y el cálculo de
// racha solo necesitan lo reciente — se acota a ~2 años. Suficiente margen
// para cualquier análisis operativo; el resto sería histórico.
const LIMITE_CIERRES = 2500;

// Todas las escrituras (crear cierre, notas, disputas) pasan por esta función
// — antes se escribía directo a Firestore desde aquí y, como la app no tiene
// Firebase Auth, cualquiera podía crear o editar el cierre de otro técnico.
// Las lecturas de este archivo (getRouteClosures, subscribeToRouteClosures...)
// siguen leyendo Firestore directo — route_closures se puede leer libremente.
async function callManageRouteClosure(payload) {
  const resp = await fetch('/.netlify/functions/manageRouteClosure', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
    body:    JSON.stringify(payload),
  });
  const data = await resp.json();
  if (!data.success) throw new Error(data.error || 'Error al guardar el cierre de ruta');
  return data;
}

/**
 * SISTEMA DE PUNTOS:
 * - Cierre básico: 5 puntos (ruta cerrada)
 * - Cierre con racha: 8 puntos (ruta cerrada + día anterior también cerrada)
 * - Cierre completo: 10 puntos (ruta cerrada con todas las órdenes completadas)
 * - Cierre completo con racha: 12 puntos (ruta completa + día anterior también cerrada)
 */

// Función helper para comparar si dos fechas son el mismo día
const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

// Función helper para obtener el día anterior
const getPreviousDay = (date) => {
  const previous = new Date(date);
  previous.setDate(previous.getDate() - 1);
  return previous;
};

// Verificar si el técnico cerró la ruta el día anterior
// Ahora recibe TODOS los cierres y busca en memoria
const checkPreviousDayClosure = (allClosures, employeeId, currentDate) => {
  const previousDay = getPreviousDay(currentDate);
  
  console.log(`\n🔍 Buscando cierre anterior para empleado ${employeeId}`);
  console.log(`Fecha actual: ${new Date(currentDate).toLocaleDateString()}`);
  console.log(`Buscando cierre del: ${previousDay.toLocaleDateString()}`);
  
  // Buscar en todos los cierres si hay uno del día anterior para este empleado
  const hasClosureYesterday = allClosures.some(closure => {
    const closureDate = closure.date?.toDate ? closure.date.toDate() : new Date(closure.date);
    const isCorrectEmployee = String(closure.employeeId) === String(employeeId);
    const isPreviousDay = isSameDay(closureDate, previousDay);
    
    if (isCorrectEmployee && isPreviousDay) {
      console.log(`✅ Encontrado cierre del día anterior:`, {
        fecha: closureDate.toLocaleDateString(),
        asignadas: closure.assigned,
        completadas: closure.completed
      });
    }
    
    return isCorrectEmployee && isPreviousDay;
  });
  
  console.log(`¿Tiene cierre el día anterior? ${hasClosureYesterday ? '✅ SÍ' : '❌ NO'}`);
  
  return hasClosureYesterday;
};

// Calcular puntos según el nuevo sistema
const calculatePoints = (closureData, employeeId, currentDate, allClosures = []) => {
  const { assigned, completed } = closureData;
  
  console.log(`\n=== 🎯 Calculando puntos ===`);
  console.log(`Empleado: ${employeeId}`);
  console.log(`Fecha: ${new Date(currentDate).toLocaleDateString()}`);
  console.log(`Asignadas: ${assigned}, Completadas: ${completed}`);
  
  // Determinar si completó todas las órdenes
  const isComplete = assigned > 0 && completed >= assigned;
  console.log(`¿Ruta completa? ${isComplete ? '✅' : '❌'}`);
  
  // Verificar si cerró el día anterior
  const hasPreviousDayClosure = checkPreviousDayClosure(allClosures, employeeId, currentDate);
  console.log(`¿Tiene racha? ${hasPreviousDayClosure ? '✅' : '❌'}`);
  
  let result;
  
  // Aplicar lógica de puntos
  if (isComplete && hasPreviousDayClosure) {
    // Ruta completa + racha = 12 puntos
    result = {
      pointsEarned: 12,
      pointsType: 'perfect_streak',
      pointsDescription: 'Ruta completa con racha',
      hasStreak: true,
      isComplete: true
    };
    console.log(`🎉 Resultado: 12 PUNTOS (Completa + Racha)`);
  } else if (isComplete) {
    // Solo ruta completa = 10 puntos
    result = {
      pointsEarned: 10,
      pointsType: 'perfect',
      pointsDescription: 'Ruta completa',
      hasStreak: false,
      isComplete: true
    };
    console.log(`⭐ Resultado: 10 PUNTOS (Solo Completa)`);
  } else if (hasPreviousDayClosure) {
    // Solo racha = 8 puntos
    result = {
      pointsEarned: 8,
      pointsType: 'streak',
      pointsDescription: 'Cierre con racha',
      hasStreak: true,
      isComplete: false
    };
    console.log(`🔥 Resultado: 8 PUNTOS (Solo Racha)`);
  } else {
    // Cierre básico = 5 puntos
    result = {
      pointsEarned: 5,
      pointsType: 'basic',
      pointsDescription: 'Cierre básico',
      hasStreak: false,
      isComplete: false
    };
    console.log(`✅ Resultado: 5 PUNTOS (Básico)`);
  }
  
  console.log(`=== Fin cálculo ===\n`);
  
  return result;
};

// Obtener todos los cierres de ruta
export const getRouteClosures = async () => {
  try {
    console.log('📥 Obteniendo todos los cierres de ruta...');
    
    const routeClosuresRef = collection(db, 'route_closures');
    const q = query(routeClosuresRef, orderBy('date', 'desc'), limit(LIMITE_CIERRES));
    const querySnapshot = await getDocs(q);
    
    console.log(`Total de cierres encontrados: ${querySnapshot.size}`);
    
    // Primero obtener todos los datos sin calcular puntos
    const allClosuresRaw = [];
    querySnapshot.forEach((docSnapshot) => {
      allClosuresRaw.push({
        id: docSnapshot.id,
        ...docSnapshot.data()
      });
    });
    
    // Ahora calcular puntos para cada uno, pasando TODOS los cierres
    const closures = allClosuresRaw.map((closure) => {
      const date = closure.date?.toDate ? closure.date.toDate() : new Date(closure.date);
      const pointsInfo = calculatePoints(closure, closure.employeeId, date, allClosuresRaw);
      
      return {
        ...closure,
        ...pointsInfo
      };
    });
    
    console.log('✅ Cálculo de puntos completado');
    
    return closures;
  } catch (error) {
    console.error('❌ Error obteniendo cierres de ruta:', error);
    throw error;
  }
};

// Obtener cierres de ruta por empleado
export const getRouteClosuresByEmployee = async (employeeId) => {
  try {
    const routeClosuresRef = collection(db, 'route_closures');
    const q = query(
      routeClosuresRef, 
      where('employeeId', '==', employeeId),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    // Obtener TODOS los cierres (no solo del empleado) para verificar rachas
    const allClosuresQuery = await getDocs(query(collection(db, 'route_closures'), orderBy('date', 'desc'), limit(LIMITE_CIERRES)));
    const allClosures = [];
    allClosuresQuery.forEach(doc => {
      allClosures.push({ id: doc.id, ...doc.data() });
    });
    
    const closures = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const date = data.toDate ? data.date.toDate() : new Date(data.date);
      const pointsInfo = calculatePoints(data, data.employeeId, date, allClosures);
      
      closures.push({
        id: docSnapshot.id,
        ...data,
        ...pointsInfo
      });
    });
    
    return closures;
  } catch (error) {
    console.error('Error obteniendo cierres por empleado:', error);
    throw error;
  }
};

// Obtener cierres de ruta por rango de fechas
export const getRouteClosuresByDateRange = async (startDate, endDate) => {
  try {
    const routeClosuresRef = collection(db, 'route_closures');
    const q = query(
      routeClosuresRef,
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    // Obtener TODOS los cierres para verificar rachas
    const allClosuresQuery = await getDocs(query(collection(db, 'route_closures'), orderBy('date', 'desc'), limit(LIMITE_CIERRES)));
    const allClosures = [];
    allClosuresQuery.forEach(doc => {
      allClosures.push({ id: doc.id, ...doc.data() });
    });
    
    const closures = [];
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const date = data.date?.toDate ? data.date.toDate() : new Date(data.date);
      const pointsInfo = calculatePoints(data, data.employeeId, date, allClosures);
      
      closures.push({
        id: docSnapshot.id,
        ...data,
        ...pointsInfo
      });
    });
    
    return closures;
  } catch (error) {
    console.error('Error obteniendo cierres por rango de fechas:', error);
    throw error;
  }
};

// Escuchar cierres de ruta en tiempo real
export const subscribeToRouteClosures = (callback, onError) => {
  const routeClosuresRef = collection(db, 'route_closures');
  const q = query(routeClosuresRef, orderBy('date', 'desc'), limit(LIMITE_CIERRES));

  return onSnapshot(q, (querySnapshot) => {
    const allClosures = [];
    querySnapshot.forEach((docSnapshot) => {
      allClosures.push({ id: docSnapshot.id, ...docSnapshot.data() });
    });
    const closures = allClosures.map((closure) => {
      const date = closure.date?.toDate ? closure.date.toDate() : new Date(closure.date);
      const pointsInfo = calculatePoints(closure, closure.employeeId, date, allClosures);
      return { ...closure, ...pointsInfo };
    });
    callback(closures);
  }, (error) => {
    console.error('Error en tiempo real:', error);
    onError?.(error);
  });
};

// Guardar notas del director sobre un cierre (uso interno, sin disputa)
export const saveRouteClosureNotes = async (closureId, notes) =>
  callManageRouteClosure({ action: 'notes', closureId, notes });

// Abrir una disputa sobre un cierre de ruta
export const openDispute = async (closureId, note) =>
  callManageRouteClosure({ action: 'openDispute', closureId, note });

// Respuesta del técnico a una disputa
export const replyToDispute = async (closureId, reply) =>
  callManageRouteClosure({ action: 'replyDispute', closureId, reply });

// Cerrar una disputa
export const closeDispute = async (closureId) =>
  callManageRouteClosure({ action: 'closeDispute', closureId });

// Crear nuevo cierre de ruta — el employeeId real lo decide el servidor a
// partir de la sesión del técnico, no lo que se mande aquí. Devuelve la
// respuesta completa ({ closure, alreadyClosed }) — el llamante necesita
// distinguir "se acaba de crear" de "ya existía" (doble toque, dos pestañas).
export const createRouteClosure = async (closureData) => {
  const payload = { action: 'create', ...closureData };
  const url = '/.netlify/functions/manageRouteClosure';

  // El cierre de ruta es de lo más crítico del día del técnico y a menudo se
  // hace en zonas sin cobertura. Si no hay red, se encola y se reintenta solo.
  // El servidor deduplica por ID determinista ({employeeId}_{fecha}), así que
  // un reenvío no crea dos cierres.
  const encolar = () => {
    enqueue({ kind: 'routeClosure', url, body: payload });
    return {
      queued: true,
      alreadyClosed: false,
      closure: { ...closureData, pointsEarned: 0, pointsDescription: 'Pendiente de sincronizar' },
    };
  };

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return encolar();
  }

  try {
    const result = await callManageRouteClosure(payload);
    console.log(`✅ Cierre ${result.alreadyClosed ? 'ya existente' : 'creado'} con ${result.closure.pointsEarned} puntos`);
    return result;
  } catch (e) {
    // Error de red (fetch rechaza) → a la cola. Un error del servidor
    // (respuesta con success:false) sí se propaga como antes.
    if (e instanceof TypeError || /fetch|network|Failed to fetch|conexión/i.test(e.message || '')) {
      return encolar();
    }
    throw e;
  }
};

// Obtener estadísticas de puntos por técnico
export const getTechnicianPointsStats = async (employeeId, startDate = null, endDate = null) => {
  try {
    let q;
    const routeClosuresRef = collection(db, 'route_closures');
    
    if (startDate && endDate) {
      q = query(
        routeClosuresRef,
        where('employeeId', '==', employeeId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        orderBy('date', 'desc')
      );
    } else {
      q = query(
        routeClosuresRef,
        where('employeeId', '==', employeeId),
        orderBy('date', 'desc')
      );
    }
    
    const querySnapshot = await getDocs(q);
    
    // Obtener TODOS los cierres para calcular rachas
    const allClosuresQuery = await getDocs(query(collection(db, 'route_closures'), orderBy('date', 'desc'), limit(LIMITE_CIERRES)));
    const allClosures = [];
    allClosuresQuery.forEach(doc => {
      allClosures.push({ id: doc.id, ...doc.data() });
    });
    
    let totalPoints = 0;
    let perfectStreakClosures = 0; // 12 puntos
    let perfectClosures = 0; // 10 puntos
    let streakClosures = 0; // 8 puntos
    let basicClosures = 0; // 5 puntos
    let totalClosures = 0;
    
    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      const date = data.date?.toDate ? data.date.toDate() : new Date(data.date);
      const pointsInfo = calculatePoints(data, employeeId, date, allClosures);
      
      totalPoints += pointsInfo.pointsEarned;
      totalClosures += 1;
      
      if (pointsInfo.pointsEarned === 12) perfectStreakClosures += 1;
      else if (pointsInfo.pointsEarned === 10) perfectClosures += 1;
      else if (pointsInfo.pointsEarned === 8) streakClosures += 1;
      else if (pointsInfo.pointsEarned === 5) basicClosures += 1;
    });
    
    return {
      totalPoints,
      totalClosures,
      perfectStreakClosures,
      perfectClosures,
      streakClosures,
      basicClosures,
      avgPointsPerDay: totalClosures > 0 ? (totalPoints / totalClosures).toFixed(1) : 0
    };
  } catch (error) {
    console.error('Error obteniendo estadísticas de puntos:', error);
    throw error;
  }
};

